import { Fragment, PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import { PlanFeatureMessage } from "../constants";

import { PlanFeature } from "./plan-feature";

const baseBorderStyles = css({
    borderInlineStartWidth: "1px",
    borderInlineEndWidth: "1px",
    borderBlockStartWidth: "0px",
    borderBlockEndWidth: "0px",
    borderStyle: "solid"
});

const planNameConatinerStyles = css({
    fontSize: "16px",
    gridRow: "1",
    lineHeight: "24px",
    padding: "20px 25px 20px",
    textAlign: "center"
});
const planNameStyles = css({
    fontSize: "24px",
    fontWeight: "600",
    lineHeight: "32px",
    margin: "0 0 8px",
    padding: "0px"
});

const ctaButtonStyles = css({
    marginBlockEnd: "32px"
});

const priceCardBodyContainerStyles = css({
    background: "white",
    display: "flex",
    flexDirection: "column",
    gridRow: "3",
    padding: "0 20px",
    textAlign: "center",
    borderBlockEndWidth: "1px !important"
});

const featureMessagesContainerStyles = css({
    "background": "white",
    "fontSize": "16px",
    "gridRow": "4",
    "lineHeight": "24px",
    "paddingBlockEnd": "20px",
    "paddingInlineEnd": "20px",
    "marginBlockEnd": "25px",
    "@media (min-width: 1024px)": { marginBlockEnd: "0px" }
});
const featureMessageTitleStyles = css({
    fontWeight: "500",
    padding: "16px 0 0 20px"
});

type PriceCardProps = {
    first?: boolean;
    last?: boolean;
    highlighted: boolean;
    planName: string;
    description?: string;
    featureMessagePlanName?: string;
    planFeatureMessages?: PlanFeatureMessage[];
    ctaButton: React.ReactNode;
};

// eslint-disable-next-line complexity
const PriceCard = ({
    first = false,
    last = false,
    highlighted = false,
    planName,
    description,
    ctaButton,
    featureMessagePlanName,
    planFeatureMessages,
    children
}: PropsWithChildren<PriceCardProps>) => {
    const jswPricingPageRedesign: string = FeatureGates.getExperimentValue(
        "bxpsu-2732-jsw-pricing-page-spork",
        "cohort",
        "control",
        { fireExperimentExposure: false }
    );

    return (
        <Fragment>
            <div
                css={[
                    baseBorderStyles,
                    planNameConatinerStyles,
                    css({
                        "borderColor": highlighted ? "#0052cc" : "#dfe1e5",
                        "backgroundColor": highlighted ? "#0052cc" : "#fafbfc",
                        "borderBlockStart": highlighted
                            ? `1px solid #0052cc`
                            : `1px solid #dfe1e5`,
                        "borderStartStartRadius": "8px",
                        "borderStartEndRadius": "8px",
                        "@media (min-width: 1024px)": {
                            borderStartStartRadius: first ? "8px" : "0px",
                            borderStartEndRadius: last ? "8px" : "0px"
                        }
                    })
                ]}
            >
                <h3
                    css={[
                        planNameStyles,
                        css({
                            color: highlighted ? "#ffffff" : "#091e42"
                        })
                    ]}
                >
                    {planName}
                </h3>

                {jswPricingPageRedesign === "fy24q4-variant-jsw-pricing" && (
                    <div
                        css={[
                            css({
                                color: highlighted ? "#ffffff" : "#091e42"
                            })
                        ]}
                    >
                        {description}
                    </div>
                )}
            </div>

            <div
                css={[
                    baseBorderStyles,
                    priceCardBodyContainerStyles,
                    css({
                        "backgroundColor": highlighted ? "#F1F7FF" : "white",
                        "borderColor": highlighted ? "#0052cc" : "#dfe1e5",
                        "borderBottomLeftRadius": !planFeatureMessages?.length
                            ? "8px"
                            : "0px",
                        "borderBottomRightRadius": !planFeatureMessages?.length
                            ? "8px"
                            : "0px",
                        "marginBlockEnd": !planFeatureMessages?.length
                            ? "25px"
                            : "0px",
                        "@media (min-width: 1024px)": {
                            borderEndStartRadius:
                                !planFeatureMessages?.length && first
                                    ? "8px"
                                    : "0px",
                            borderEndEndRadius:
                                !planFeatureMessages?.length && last
                                    ? "8px"
                                    : "0px"
                        }
                    })
                ]}
            >
                {children}
                <div css={ctaButtonStyles}>{ctaButton}</div>
            </div>
            {planFeatureMessages && planFeatureMessages.length > 0 && (
                <div
                    css={[
                        featureMessagesContainerStyles,
                        baseBorderStyles,
                        css({
                            "borderColor": highlighted ? "#0052cc" : "#dfe1e5",
                            "borderBlockEnd": highlighted
                                ? `1px solid #0052cc`
                                : `1px solid #dfe1e5`,
                            "borderBottomLeftRadius": "8px",
                            "borderBottomRightRadius": "8px",
                            "@media (min-width: 1024px)": {
                                borderEndStartRadius: first ? "8px" : "0px",
                                borderEndEndRadius: last ? "8px" : "0px"
                            }
                        })
                    ]}
                >
                    {featureMessagePlanName && (
                        <div css={featureMessageTitleStyles}>
                            <FormattedMessage
                                id="Pricing.PriceCard.featureMessageTitle"
                                defaultMessage="Everything from {featureMessagePlanName} plus:"
                                values={{ featureMessagePlanName }}
                            />
                        </div>
                    )}
                    {planFeatureMessages.map((planFeatureMessage, idx) => (
                        <PlanFeature key={idx} {...planFeatureMessage} />
                    ))}
                </div>
            )}
        </Fragment>
    );
};

export default PriceCard;
